// Translation lookup only. This module must stay free of session/store imports so
// that the many modules reaching it through the @/utils/i18n barrel do not inherit
// the session layer's dependency graph.
//
// Locale *resolution* (which reads the active user and subscription) lives in
// i18n-locale-resolver.js.
import { createPendoI18n, normalizeLocale } from '@pendo/i18n';
import * as Sentry from '@sentry/vue';
import { normalizedLocale } from './use-i18n';
import momentTimeZone from 'moment-timezone';

// changeLocale returns early for the current locale, so start on a placeholder to let even en load.
const UNLOADED_LOCALE = 'und';

momentTimeZone.locale('en');
const i18n = createPendoI18n({
    locale: UNLOADED_LOCALE,
    missingWarn: false,
    fallbackWarn: false,
});

const reportedMissingKeys = new Set();

function isLocaleLoaded () {
    return i18n.global.locale.value !== UNLOADED_LOCALE;
}

function reportMissingKey (locale, key) {
    // Until the first locale loads, every lookup misses in und and the en fallback.
    if (!isLocaleLoaded() || reportedMissingKeys.has(key)) {
        return;
    }

    reportedMissingKeys.add(key);

    if (__PROCESS__.LOCAL_ENV) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] Missing translation for "${key}" in locale "${locale}"`);
    } else {
        Sentry.captureMessage(`Missing translation for "${key}" in locale "${locale}"`, 'warning');
    }
}

i18n.global.setMissingHandler(reportMissingKey);

const { i18n_t } = i18n;

const i18n_locale = normalizedLocale(i18n.global.locale);

const loadLocaleMessages = (locale) => import(`@/locales/${normalizeLocale(locale)}.json`);

export { i18n, i18n_t, i18n_locale, loadLocaleMessages, isLocaleLoaded };
